import { useState, useEffect } from 'react';
import { Modal } from './Modal';
import { LeadForm } from './LeadForm';
import { Button } from './Button';
import { ActivityTimeline } from './ActivityTimeline';
import { useLanguage } from '../hooks/useLanguage';
import { getLeadEventHistory } from '../services/api';

const DEFAULT_MODAL_FIELDS = ['full_name', 'phone', 'message', 'ad_name', 'campaign_name', 'created_at', 'assigned_to', 'source'];

export const LeadDetailModal = ({ lead, isOpen, onClose, onUpdate, onDelete }) => {
  const [visibleFields, setVisibleFields] = useState(DEFAULT_MODAL_FIELDS);
  const [eventHistory, setEventHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const { t, language } = useLanguage();

  useEffect(() => {
    const savedFields = localStorage.getItem('kanban_modal_fields');
    if (savedFields) {
      setVisibleFields(JSON.parse(savedFields));
    }
  }, []);

  useEffect(() => {
    const fetchEventHistory = async () => {
      if (lead && isOpen) {
        setLoadingHistory(true);
        try {
          const history = await getLeadEventHistory(lead.id);
          setEventHistory(history);
        } catch (error) {
          console.error('Failed to load event history:', error);
        } finally {
          setLoadingHistory(false);
        }
      }
    };
    fetchEventHistory();
  }, [lead, isOpen]);

  const handleUpdateSuccess = () => {
    onUpdate();
    // Refresh event history after update
    if (lead) {
      getLeadEventHistory(lead.id).then(setEventHistory).catch(console.error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatEventDate = (dateString) => {
    const date = new Date(dateString);
    const locale = language === 'he' ? 'he-IL' : 'en-US';
    return date.toLocaleString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  const getAttendanceStatusLabel = (status) => {
    const statusMap = {
      attended: t('attended') || 'הגיע',
      absent: t('absent') || 'לא הגיע',
      no_show: t('noShow') || 'לא הופיע',
      cancelled: t('cancelled') || 'בוטל',
      pending: t('pending') || 'ממתין'
    };
    return statusMap[status] || status;
  };

  const getAttendanceStatusColor = (status) => {
    const colorMap = {
      attended: 'bg-green-100 text-green-800',
      absent: 'bg-red-100 text-red-800',
      no_show: 'bg-orange-100 text-orange-800',
      cancelled: 'bg-gray-100 text-gray-800',
      pending: 'bg-yellow-100 text-yellow-800'
    };
    return colorMap[status] || 'bg-gray-100 text-gray-800';
  };

  const getFieldLabel = (field) => {
    const labelMap = {
      full_name: t('fullNameLabel'),
      phone: t('phoneLabel'),
      message: t('messageLabel'),
      ad_name: t('adName'),
      campaign_name: t('campaignName'),
      adset_name: t('adsetName'),
      form_id: t('formId'),
      ad_id: t('adId'),
      campaign_id: t('campaignId'),
      fb_lead_id: t('fbLeadId'),
      created_time: t('createdTime'),
      created_at: t('createdDate'),
      assigned_to: t('assignedToLabel'),
      source: t('sourceLabel'),
    };
    return labelMap[field] || field.replace(/_/g, ' ').split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  if (!lead) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('leadDetails') || 'Lead Details'}
    >
      <div className="space-y-6">
        {/* Lead Form */}
        <div>
          <LeadForm
            lead={lead}
            onSuccess={handleUpdateSuccess}
            onCancel={onClose}
          />
        </div>

        {/* Event History */}
          <div className="pt-4 border-t border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">
              {t('eventHistory')}
            </h3>
            {loadingHistory ? (
              <div className="text-center py-4 text-gray-500">{t('loading')}</div>
            ) : eventHistory.length === 0 ? (
              <div className="text-center py-4 text-gray-500">
                {t('noEventHistory')}
              </div>
            ) : (
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {eventHistory.map((event) => (
                  <div
                    key={event.event_id}
                    className={`p-3 rounded-lg border ${
                      event.is_past_event ? 'bg-gray-50 border-gray-200' : 'bg-blue-50 border-blue-200'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-medium text-gray-900">{event.event_name}</h4>
                          {!event.is_past_event && (
                            <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700">
                              {t('upcoming')}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600">
                          {formatEventDate(event.event_date)}
                          {event.activity_name && ` • ${event.activity_name}`}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        {event.attendance_status && (
                          <span
                            className={`text-xs px-2 py-1 rounded font-medium ${getAttendanceStatusColor(
                              event.attendance_status
                            )}`}
                          >
                            {getAttendanceStatusLabel(event.attendance_status)}
                          </span>
                        )}
                      </div>
                    </div>
                    {event.marked_at && (
                      <p className="text-xs text-gray-500 mt-2">
                        {t('markedBy')}: {event.marked_by_name || 'Unknown'} •{' '}
                        {formatDate(event.marked_at)}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

        {/* Activity Timeline */}
        <div className="pt-4 border-t border-gray-200">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">
            {t('activityTimeline')}
          </h3>
          <ActivityTimeline leadId={lead.id} />
        </div>
      </div>
    </Modal>
  );
};
